import type { AutomaticOutreachWorkflow } from '#services/api/mia.ts';
import type { GetMeResponsePayload } from '#services/api/user.ts';

import { config } from '#config.ts';

/**
 * Forces the agent on for a local-dev host, so the install can be exercised without deploying.
 */
const enableParam = 'pendo';
const enableParamValue = '1';

const localDevHosts = new Set(['127.0.0.1', 'localhost', 'insights.mendvip.local']);

/** Pendo requires an account name; orgs that have never set one still need a stable label. */
const unnamedOrgLabel = 'None Set';

interface PendoEvents {
  coordinate_automatic_outreach_dialog_opened: { workflow: AutomaticOutreachWorkflow };
  coordinate_automatic_outreach_saved: {
    workflow: AutomaticOutreachWorkflow;
    enabled: boolean;
  };
  coordinate_bucket_selected: { bucket: 'reschedule' | 'unconfirmed' | 'waitlist' };
  coordinate_confirmation_dialog_opened: {
    bucket: 'unconfirmed';
    appointment_count: number;
    action_scope: 'card' | 'row';
    source?: 'briefing';
  };
  coordinate_confirmation_requests_sent: {
    bucket: 'unconfirmed';
    appointment_count: number;
    action_scope: 'card' | 'row';
  };
  coordinate_data_agent_opened: {
    source: 'briefing_ask_coordinate' | 'briefing_why_patients';
    agent_status: 'auto-pending' | 'auto-ready' | 'auto-viewed';
  };
  coordinate_rebooking_dialog_opened: {
    bucket: 'reschedule';
    appointment_count: number;
    excluded_count: number;
    action_scope: 'card' | 'row';
  };
  coordinate_rebooking_outreach_sent: {
    bucket: 'reschedule';
    appointment_count: number;
    excluded_count?: number;
    action_scope: 'card' | 'row';
  };
  coordinate_risk_explanation_ready: { ms_since_tab_opened: number };
  coordinate_risk_model_opened: { risk_kind: 'late_cancel' | 'no_show' };
  coordinate_slot_offer_dialog_opened: {
    bucket: 'waitlist';
    appointment_count: number;
    candidate_count: number;
    action_scope: 'card' | 'row';
  };
  coordinate_slot_offers_sent: {
    bucket: 'waitlist';
    appointment_count: number;
    candidate_count: number;
    action_scope: 'card' | 'row';
  };
  coordinate_tab_opened: undefined;
  coordinate_threshold_saved: { threshold: number };
  coordinate_waitlist_registered: undefined;
}

let isAgentInstalled = false;
let isVisitorIdentified = false;

/**
 * Pendo serves the agent from a Mend-owned host per registrable domain, so
 * `insights.mendfamily.com`, `insights-preprod/-uat.mend.com` and `insights-dev3/4.mendvip.com`
 * each fetch it from their own `pendo-content` CNAME. Anything unrecognized is a dev host.
 */
function getContentDomain(hostname: string): 'mend.com' | 'mendfamily.com' | 'mendvip.com' {
  if (hostname.endsWith('mendfamily.com')) {
    return 'mendfamily.com';
  }

  if (hostname.endsWith('mend.com')) {
    return 'mend.com';
  }

  return 'mendvip.com';
}

function shouldLoadAgent(location: Location): boolean {
  if (!localDevHosts.has(location.hostname)) {
    return true;
  }

  return new URLSearchParams(location.search).get(enableParam) === enableParamValue;
}

/**
 * Pendo's install snippet: a placeholder that buffers calls until the real agent replaces it and
 * replays `_q`.
 */
function createStubQueue(): NonNullable<Window['pendo']> {
  const queue: unknown[][] = [];

  return {
    _q: queue,
    initialize: (initializeConfig) => {
      queue.push(['initialize', initializeConfig]);
    },
    track: (eventName, properties) => {
      queue.push(['track', eventName, properties]);
    },
  };
}

/**
 * Load Mend's Pendo agent.
 *
 * Must run after `token-bootstrap` has rewritten the URL: an MBC authenticated link arrives with a
 * live session JWT in `?token=`, and the agent captures the page URL it finds.
 */
export function installPendoAgent(): void {
  if (isAgentInstalled || !shouldLoadAgent(window.location)) {
    return;
  }

  window.pendo ??= createStubQueue();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://pendo-content.${getContentDomain(window.location.hostname)}/agent/static/${config.PENDO_AGENT_KEY}/pendo.js`;
  // A stranded queue looks exactly like an app nobody used, so say so rather than fail silently.
  script.onerror = () => {
    console.error('pendo: agent script failed to load; buffered calls were never sent');
  };

  document.head.appendChild(script);

  isAgentInstalled = true;
}

/**
 * Identify the signed-in user as a Pendo visitor, and their org as the account.
 *
 * Safe to call on every navigation because the local guard initializes Pendo only once.
 */
export function identifyPendoVisitor(me: GetMeResponsePayload): void {
  if (isVisitorIdentified || !isAgentInstalled) {
    return;
  }

  window.pendo?.initialize({
    visitor: {
      id: me.user.id,
      userId: me.user.id,
      role: me.user.role,
      orgId: me.org.id,
      email: me.user.email,
    },
    account: {
      id: me.org.id,
      orgId: me.org.id,
      role: me.user.role,
      orgName: me.org.name || unnamedOrgLabel,
    },
  });

  isVisitorIdentified = true;
}

export function trackPendoEvent<EventName extends keyof PendoEvents>(
  eventName: EventName,
  ...[properties]: PendoEvents[EventName] extends undefined
    ? [properties?: undefined]
    : [properties: PendoEvents[EventName]]
): void {
  window.pendo?.track(eventName, properties);
}
